import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Accordion from '~/common/components/Accordions/Accordion';
import AccordionButton from '~/common/components/Accordions/AccordionButton';
import AccordionIcon from '~/common/components/Accordions/AccordionIcon';
import AccordionItem from '~/common/components/Accordions/AccordionItem';
import AccordionPanel from '~/common/components/Accordions/AccordionPanel';
import Box from '~/common/components/Box';
import Button from '~/common/components/Buttons/Button';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';

type ContentsProps = {
  header?: string;
  children: React.ReactNode | React.ReactNode[];
};

function Contents({ header, children }: Readonly<ContentsProps>) {
  return (
    <Box p={3} pl={6} pr={6} id={`calculation-breakdown-${header}`}>
      {children}
    </Box>
  );
}

type BreakdownContainerProps = {
  /** The header text to display for the breakdown */
  header?: string;
  /** The subheader text to display for the breakdown */
  subheader?: string;
  /** The calculated amount to display on the accordion header */
  calculatedAmount?: number;
  /** A click handler for performing an action when the user requests to see the breakdown in a popout window.
   * Settings this value is only required for session history. */
  onNewWindow?: () => void;
  hideAccordion?: boolean;
  children: React.ReactNode | React.ReactNode[];
};

const BreakdownContainer = ({
  calculatedAmount,
  header = 'Calculation Breakdown',
  subheader,
  onNewWindow,
  hideAccordion = false,
  children,
}: Readonly<BreakdownContainerProps>) => {
  if (hideAccordion) {
    return <Contents header={header}>{children}</Contents>;
  }

  return (
    <Accordion
      defaultIndex={onNewWindow ? 0 : undefined}
      allowToggle={!onNewWindow}
      as="article"
      allowMultiple
    >
      <AccordionItem
        border="solid 1px #E2E8F0"
        borderRadius="4px"
        overflow="hidden"
      >
        <AccordionButton bg="gray.50" _hover={{ backgroundColor: 'gray.50' }}>
          <Box display="inline-flex" flex="1" textAlign="left">
            <Text as="h3" fontWeight="bold">
              {header}
            </Text>
            {subheader && <span>&nbsp;{subheader}</span>}
          </Box>
          {typeof calculatedAmount === 'number' && (
            <Box as="h3" mr={5} textAlign="right" fontWeight="bold">
              {formatCurrency(calculatedAmount)}
            </Box>
          )}
          {!onNewWindow && <AccordionIcon />}
          {onNewWindow && (
            <Button
              display="none"
              variant="link"
              colorScheme="blue"
              fontWeight={100}
              rightIcon={<FontAwesomeIcon icon={faExternalLink} />}
              onClick={onNewWindow}
            >
              Open in new window
            </Button>
          )}
        </AccordionButton>
        <AccordionPanel pb={4}>
          <Contents header={header}>{children}</Contents>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default BreakdownContainer;
